import { enableProdMode, importProvidersFrom, isDevMode, provideZonelessChangeDetection, ErrorHandler } from '@angular/core';
import { HTTP_INTERCEPTORS } from '@angular/common/http';

import { environment } from './environments/environment';
import { routes } from './app.routes';

import { PreloadAllModules, provideRouter, RouteReuseStrategy, withPreloading } from '@angular/router';
import { IonicRouteStrategy, IonicModule } from '@ionic/angular';
import { CognitoConfigService, StorageService, AuthModule } from '@uoa/auth';
import { AppAuthConfigService, AppStorageService } from './app/core/services';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { provideErrorPages } from '@uoa/error-pages';
import { provideServiceWorker } from '@angular/service-worker';
import { IonicStorageModule } from '@ionic/storage-angular';
import { AppComponent } from './app/app.component';
import { CatchitErrorHandlerService } from './app/core/services/catchit-error-handler.service';
import { ErrorInterceptor } from './app/core/interceptors/error-interceptor';
import { provideOfflineSafeRefresh } from './app/core/auth/offline-safe-refresh';

if (environment.production) {
  enableProdMode();
}

// Suppresses a known crash inside @uoa/auth's own background token-refresh interceptor:
// it unconditionally reads decodedToken['identities'][0].userId, a claim that only exists
// on SAML/SSO tokens - throws for native/external accounts (see cognito-user.service.ts's
// loadCurrentUser() for the full writeup, which already works around this for CatchIT's
// own code by decoding the ID token itself rather than calling into the library). This is
// the library's own internal interceptor, outside app code entirely, and fires repeatedly -
// confirmed live once every ~10s poll cycle for the duration of a native-account session,
// not a rare one-off. Zoneless (provideZonelessChangeDetection() below - no Zone.js), so
// this reaches the browser's native unhandledrejection event same as any other uncaught
// rejection, surfacing to users as CatchitErrorHandlerService's "Critical Error" dialog and
// @uoa/error-pages' redirect to /error/500 - neither of which reflects a problem with
// whatever request triggered it (confirmed live: the triggering request had already
// succeeded by the time this fires). Matched on message alone, not source file/stack -
// "Cannot read properties of undefined (reading '0')" is distinctive enough in this app's
// context, and a minified production stack trace isn't guaranteed to still contain a
// recognisable "uoa-auth" substring. Registered before bootstrapApplication() so it runs
// ahead of any other unhandledrejection listener Angular/uoa libraries attach on startup.
window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
  const reason: unknown = event.reason;
  const message = reason instanceof Error ? reason.message : String(reason);
  if (message.includes("Cannot read properties of undefined (reading '0')")) {
    event.preventDefault();
    event.stopImmediatePropagation();
    console.warn('Suppressed known @uoa/auth token-refresh crash for a native/non-SSO account.');
  }
});

// @uoa/auth's LoginService.doLogin() builds and navigates to the Cognito authorize URL
// entirely internally (no config hook for extra query params), always landing on the Hosted
// UI's provider-picker screen. Cognito supports skipping straight to a named federated IdP via
// an `identity_provider` param on that same request. This used to be patched locally inside
// login.page.ts's own click handler, restoring window.open after the first call - but that
// only covered the login button's own explicit call path. @uoa/auth's AuthGuard.canActivate()
// can independently trigger the exact same redirect (e.g. when a protected route resolves
// before/alongside the login page's own attempt) via its own call to obtainValidAccessToken(),
// bypassing the button entirely - confirmed live via two separate PKCE code_challenge values
// racing each other on a single login attempt, one from each path, with the unpatched
// guard-triggered one frequently winning on mobile. Patching window.open globally, once, here
// at bootstrap (not self-restoring) covers every current and future caller of Cognito's
// authorize endpoint, regardless of which code path triggers it.
if (environment.auth.identityProvider) {
  const identityProvider = environment.auth.identityProvider;
  const originalOpen = window.open.bind(window);
  window.open = ((url?: string | URL, target?: string, features?: string) => {
    if (typeof url === 'string' && url.includes('/oauth2/authorize') && !url.includes('identity_provider=')) {
      const separator = url.includes('?') ? '&' : '?';
      url += `${separator}identity_provider=${encodeURIComponent(identityProvider)}`;
    }
    return originalOpen(url as string, target, features);
  }) as typeof window.open;
}

bootstrapApplication(AppComponent, {
    providers: [
        provideZonelessChangeDetection(),
        importProvidersFrom(BrowserModule, IonicModule.forRoot(), AuthModule, IonicStorageModule.forRoot()),
        provideErrorPages(),
        provideRouter(routes, withPreloading(PreloadAllModules)),
        // isDevMode() (not environment.production) - environment.dev.ts sets production:
        // false even though its build is fully optimized/AOT and deployed to CloudFront,
        // so gating on that flag would leave dev permanently without a service worker.
        // isDevMode() reflects whether this is an optimized build, and is false there.
        provideServiceWorker('ngsw-worker.js', {
            enabled: !isDevMode(),
            registrationStrategy: 'registerWhenStable:30000'
        }),
        { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
        { provide: CognitoConfigService, useClass: AppAuthConfigService },
        { provide: StorageService, useClass: AppStorageService },
        { provide: ErrorHandler, useClass: CatchitErrorHandlerService },
        { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
        provideOfflineSafeRefresh(),
    ]
}).catch((err) => console.error(err));
